import React, { useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import AdDisplay from '@/components/ads/AdDisplay';
import SupportButtons from '@/components/support/SupportButtons';
import { useIsMobile } from '@/hooks/use-mobile';
import { createGameSlug } from '@/utils/gameUtils';
import { SEO } from '@/components/SEO';
import ContentFreshness from '@/components/seo/ContentFreshness';
import MobileOptimization from '@/components/seo/MobileOptimization';
import XMLSitemap from '@/components/seo/XMLSitemap';
import GameDetailSkeleton from '@/components/game-detail/GameDetailSkeleton';
import GameNotFound from '@/components/game-detail/GameNotFound';
import SharePopoverContent from '@/components/game-detail/SharePopoverContent';
import GamePlayer from '@/components/game-detail/GamePlayer';
import LandscapeGamePlayer from '@/components/game-detail/LandscapeGamePlayer';
import GameInfo from '@/components/game-detail/GameInfo';
import RelatedGames from '@/components/game-detail/RelatedGames';
import GameSidebar from '@/components/game-detail/GameSidebar';
import { useGameData } from '@/hooks/useGameData';
import { useGameSession } from '@/hooks/useGameSession';
import { useGamePresence } from '@/hooks/useGamePresence';

const GameDetail = () => {
  const { gameId, gameSlug } = useParams<{ gameId?: string; gameSlug?: string }>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showLandscapePlayer, setShowLandscapePlayer] = useState(false);
  const isMobile = useIsMobile();
  
  // Scroll to top when component mounts or game changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [gameId, gameSlug]);
  
  // Simplified routing logic - be more explicit about what we're searching for
  const isUuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  let searchParam: string | undefined;
  let isSlugRoute: boolean;
  
  // Handle different route patterns
  if (gameId && gameSlug) {
    // Route: /game/:gameId/:gameSlug
    if (isUuidPattern.test(gameId)) {
      // gameId is actually a UUID
      searchParam = gameId;
      isSlugRoute = false;
      console.log('Using ID from gameId param:', gameId);
    } else {
      // gameId is actually a slug, gameSlug might be extra
      searchParam = gameId;
      isSlugRoute = true;
      console.log('Using slug from gameId param:', gameId);
    }
  } else if (gameId && !gameSlug) {
    // Route: /game/:gameId
    if (isUuidPattern.test(gameId)) {
      // It's a UUID
      searchParam = gameId;
      isSlugRoute = false;
      console.log('Using ID from single param:', gameId);
    } else {
      // It's a slug
      searchParam = gameId;
      isSlugRoute = true;
      console.log('Using slug from single param:', gameId);
    }
  } else if (!gameId && gameSlug) {
    // Route: /game/:gameSlug (shouldn't happen with current routing)
    searchParam = gameSlug;
    isSlugRoute = true;
    console.log('Using gameSlug param:', gameSlug);
  }
  
  console.log('GameDetail final params:', { 
    gameId, 
    gameSlug, 
    searchParam, 
    isSlugRoute,
    route: window.location.pathname 
  });
  
  const { game, isLoading, error, relatedGames, incrementPlayCount } = useGameData(searchParam, isSlugRoute);
  
  useGameSession(game, isPlaying);
  useGamePresence(game);

  const handlePlayGame = useCallback(() => {
    if (game) {
      console.log('Playing game:', game.title, 'ID:', game.id);
      incrementPlayCount.mutate(game.id);
    }
    
    if (isMobile) {
      setShowLandscapePlayer(true);
    } else {
      setIsPlaying(true);
    }
  }, [game, incrementPlayCount, isMobile]);

  const handleExitLandscapePlayer = useCallback(() => {
    setShowLandscapePlayer(false);
  }, []);

  if (isLoading) {
    return <GameDetailSkeleton />;
  }

  if (error || !game) {
    console.error('Game not found error:', error);
    return <GameNotFound />;
  }

  // Verify we have the right game
  console.log('Displaying game:', {
    id: game.id,
    title: game.title,
    searchParam,
    isSlugRoute
  });

  const displayPlayCount = game.fakePlayCount ?? game.playCount ?? 0;
  const gameSlugFromTitle = createGameSlug(game.title);
  const canonicalUrl = `/game/${gameSlugFromTitle}`;

  return (
    <MobileOptimization>
      <SEO 
        pageType="game"
        pageIdentifier={game.id}
        title={`${game.title} - Play Free Online Game`}
        description={game.description || `Play ${game.title} for free on GameHub. ${displayPlayCount.toLocaleString()} plays and ${game.rating} star rating.`}
        keywords={`${game.title}, ${game.category} games, ${game.tags?.join(', ')}, free online games`}
        image={game.thumbnail}
        type="game"
        gameData={{
          ...game,
          playCount: displayPlayCount,
          updatedAt: game.updatedAt,
          createdAt: game.createdAt
        }}
        lastModified={game.updatedAt}
        url={canonicalUrl}
      />

      <XMLSitemap />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {showLandscapePlayer ? (
          <LandscapeGamePlayer 
            game={game} 
            onExit={handleExitLandscapePlayer}
          />
        ) : (
          <>
            <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                <div className="flex items-center justify-between">
                  <Link 
                    to="/" 
                    className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors font-medium"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Games
                  </Link>
                  
                  <div className="flex items-center space-x-3">
                    <ContentFreshness
                      lastUpdated={game.updatedAt}
                      playCount={displayPlayCount}
                      isNew={game.isNew}
                      isTrending={game.trending}
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-gray-600 hover:text-blue-500"
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <SharePopoverContent gameTitle={game.title} />
                    </Popover>
                  </div>
                </div>
              </div>
            </div>

            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8`}>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
                <div className="lg:col-span-3 space-y-6">
                  <AdDisplay position="before-game" className="w-full" />
                  <GamePlayer game={game} isPlaying={isPlaying} onPlayClick={handlePlayGame} />
                  <AdDisplay position="after-game" className="w-full" />
                  
                  {/* Support Section */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Enjoying the game?</h3>
                        <p className="text-gray-600">Support us to keep bringing you more awesome games!</p>
                      </div>
                      <SupportButtons />
                    </div>
                  </div>
                  
                  <GameInfo game={game} />
                  {relatedGames.length > 0 && <RelatedGames relatedGames={relatedGames} categoryName={game.category} />}
                </div>
                <GameSidebar game={game} onPlay={handlePlayGame} />
              </div>
            </div>
          </>
        )}
      </div>
    </MobileOptimization>
  );
};

export default GameDetail;
